import React, { useEffect, useState } from 'react'
import { FaBath } from "react-icons/fa";
import { IoBedSharp } from "react-icons/io5";
import { MdOutlineLandscape, MdOutlineSquareFoot } from "react-icons/md";
import { useParams } from 'react-router-dom';
import authServices from '../services/AuthServices';
import Loading from '../components/feedback/Loading';
import FetchError from '../components/feedback/FetchError';
import { Helmet } from 'react-helmet';
import utils from '../utils';
import { RenderIcon } from '../components/houses/IconRenderer';
import BookingModal from '../components/houses/BookingsModal';

function PropertyGallery({ property }) {
    const [activeIndex, setActiveIndex] = useState(0)
    const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)

    const normalizedCover = React.useMemo(() => {
        if (!property.cover_image) return '';

        try {
            const url = new URL(property.cover_image);
            return url.pathname;
        } catch (e) {
            return property.cover_image.startsWith('/media')
                ? property.cover_image
                : `/media/${property.cover_image.replace(/^\/?/, '')}`;
        }
    }, [property.cover_image]);

    const galleryImages = Array.isArray(property.gallery_urls) ? property.gallery_urls : []

    const allImageUrls = galleryImages.map(img => img.url)
    const allImages = [normalizedCover, ...allImageUrls].filter(Boolean)

    if (allImages.length === 0) return null

    return (
        <>
            <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 h-80 overflow-hidden rounded-xl">
                    <img
                        alt={property.title || 'Cover'}
                        className="w-full h-full object-cover"
                        src={`${utils.baseUrl}${allImages[0]}`}
                        loading="lazy"
                    />
                </div>

                {allImages.slice(1, 3).map((url, idx) => (
                    <div key={idx} className="h-48 overflow-hidden rounded-xl">
                        <img
                            alt={`Thumbnail ${idx + 2}`}
                            className="w-full h-full object-cover"
                            src={`${utils.baseUrl}${url}`}
                            loading="lazy"
                        />
                    </div>
                ))}
            </div>
            <button
                type="button"
                onClick={() => setIsFullscreenOpen(true)}
                className="mt-6 flex items-center justify-center gap-2 w-full px-6 py-4 bg-white border border-outline-variant/20 rounded-xl text-primary font-semibold uppercase text-xs tracking-widest hover:bg-surface-container-low transition-colors"
            >
                View All Photos ({allImages.length})
            </button>
            {isFullscreenOpen && (
                <div
                    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
                    onClick={() => setIsFullscreenOpen(false)}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            setIsFullscreenOpen(false)
                        }}
                        className="absolute top-4 right-4 z-[101] w-12 h-12 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center hover:bg-white/20 transition-all"
                        aria-label="Close"
                    >
                        <span className="text-2xl">×</span>
                    </button>
                    <div className="max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center">
                        <img
                            src={`${utils.baseUrl}${allImages[activeIndex]}`}
                            alt={`Full view ${activeIndex + 1}`}
                            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                            loading="lazy"
                        />

                        <div className="flex items-center gap-4 mt-6">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setActiveIndex(prev => (prev > 0 ? prev - 1 : allImages.length - 1))
                                }}
                                className="w-12 h-12 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center hover:bg-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                disabled={activeIndex === 0}
                                aria-label="Previous image"
                            >
                                ←
                            </button>

                            <span className="text-white font-medium text-sm">
                                {activeIndex + 1} / {allImages.length}
                            </span>

                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setActiveIndex(prev => (prev < allImages.length - 1 ? prev + 1 : 0))
                                }}
                                className="w-12 h-12 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center hover:bg-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                disabled={activeIndex === allImages.length - 1}
                                aria-label="Next image"
                            >
                                →
                            </button>
                        </div>

                        {allImages.length > 1 && (
                            <div className="flex gap-2 mt-6 py-3 overflow-x-auto pb-2 px-2 max-w-full custom-scrollbar">
                                {allImages.map((url, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setActiveIndex(idx)
                                        }}
                                        className={`relative flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden transition-all ${activeIndex === idx
                                            ? 'ring-2 ring-white scale-105'
                                            : 'opacity-60 hover:opacity-100'
                                            }`}
                                    >
                                        <img
                                            src={`${utils.baseUrl}${url}`}
                                            alt=""
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

function HouseViewPage() {
    const { slug } = useParams();
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [property, setProperty] = useState()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    const getProperty = (slug) => {
        setError()
        setLoading(true)
        authServices.getPropertyDetails(slug)
            .then(res => setProperty(res.data))
            .catch(err => setError(
                { message: 'Failed to load property details.' }
            )
            )
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        if (slug) {
            getProperty(slug)
        }
    }, [slug])
    return (
        <div>
            {loading && (<Loading />)}
            {error && (
                <div className='mt-8 max-w-7xl mx-auto px-8'>
                    <FetchError error={error} />
                </div>
            )}
            {property && (
                <>
                    <Helmet>
                        <title>{property?.title} - {utils.siteName}</title>
                        <meta name="description" content={property?.description} />
                    </Helmet>
                    <div className=" space-y-16">
                        <section className="mt-8 max-w-7xl mx-auto px-8">
                            <div className="relative h-[450px] md:h-[550px] w-full overflow-hidden rounded-2xl group shadow-2xl">
                                <img
                                    alt={property?.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                                    data-alt={property?.title}
                                    src={property?.cover_image}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                <div className="absolute bottom-0 left-0 w-full p-8 md:p-12 text-white">
                                    <h1 className="font-headline text-3xl md:text-4xl font-bold mb-4 leading-tight">
                                        {property?.title}
                                    </h1>
                                    <div className="flex items-center gap-6">
                                        <span className="text-lg md:text-xl font-light opacity-90 italic">
                                            Starting from KSH. {property?.nightly_rate} / night
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <section className="max-w-7xl mx-auto px-8">
                            <div className="bg-surface-container rounded-2xl py-6 px-4 flex flex-wrap justify-around gap-6 text-primary border border-gray-100">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-secondary"><IoBedSharp className='h-5 w-5' /></span>
                                    <span className="font-label text-xs uppercase tracking-widest font-bold">{property?.bedrooms} Bedrooms</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-secondary"><FaBath className='h-4 w-4' /></span>
                                    <span className="font-label text-xs uppercase tracking-widest font-bold">{property?.bathrooms} Bathrooms</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-secondary"><MdOutlineSquareFoot className='h-6 w-6' /></span>
                                    <span className="font-label text-xs uppercase tracking-widest font-bold">{property?.size_sqm} SQFT</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-secondary"><MdOutlineLandscape className='h-6 w-6' /></span>
                                    <span className="font-label text-xs uppercase tracking-widest font-bold">Lake View</span>
                                </div>
                            </div>
                        </section>

                        <section className="max-w-7xl mx-auto px-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                                <div className="order-2 lg:order-1">
                                    <p className="font-label text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-4">
                                        Find Your Sanctuary
                                    </p>
                                    <div className="space-y-6 text-text-muted leading-relaxed text-lg font-light">
                                        <p>
                                            {property?.description}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-8 mt-12 pt-3 border-t border-gray-100">
                                        <div>
                                            <h4 className="font-bold text-primary text-sm uppercase tracking-wider mb-2">Check-in</h4>
                                            <p className="text-text-muted">From {property?.check_in_time}</p>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-primary text-sm uppercase tracking-wider mb-2">Check-out</h4>
                                            <p className="text-text-muted">By {property?.check_out_time}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setIsBookingOpen(true)}
                                        className="btn btn-secondary btn-block rounded-xl mt-12"
                                    >
                                        Book Your Stay
                                    </button>
                                </div>
                                <div className="order-1 lg:order-2">
                                    <PropertyGallery property={property} />
                                </div>
                            </div>
                        </section>

                        <section className="bg-surface-container py-16 border-y border-gray-100">
                            <div className="max-w-7xl mx-auto px-8">
                                <div className="text-center mb-16">
                                    <p className="font-label text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-4">
                                        Curated Comfort
                                    </p>
                                    <h2 className="font-headline text-3xl md:text-4xl text-primary">Amenities of Note</h2>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {property?.amenities?.map(amenity => (
                                        <div
                                            key={amenity?.id}
                                            className="bg-white p-8 rounded-2xl border border-gray-100 flex items-start gap-5 hover:shadow-md transition-shadow">
                                            <div className="w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center text-primary shrink-0">
                                                <span className="material-symbols-outlined"><RenderIcon iconName={amenity?.icon_class} /></span>
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-primary text-sm uppercase tracking-wider mb-2">{amenity?.name}</h4>
                                                <p className="text-text-muted text-sm leading-relaxed">{amenity?.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                        <BookingModal
                            isOpen={isBookingOpen}
                            onClose={() => setIsBookingOpen(false)}
                            mode="create"
                            propertyId={property.id} 
                            nightlyRate={property?.nightly_rate}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default HouseViewPage